import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { PermissionName } from '../types/roles';

interface PermissionsData {
  globalPermissions: PermissionName[];
  channelPermissions: { [channelId: string]: PermissionName[] };
}

export function usePermissions() {
  const [permissions, setPermissions] = useState<PermissionsData>({
    globalPermissions: [],
    channelPermissions: {},
  });
  const [loading, setLoading] = useState(true);

  const fetchPermissions = useCallback(async () => {
    try {
      const response = await api.get('/auth/permissions');
      setPermissions(response.data);
    } catch (error) {
      console.error('Error fetching permissions:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasPermission = useCallback(
    (permission: PermissionName, channelId?: string): boolean => {
      // Check global permissions first
      if (permissions.globalPermissions.includes(permission)) {
        return true;
      }

      // Check channel-specific permissions if channelId provided
      if (channelId && permissions.channelPermissions[channelId]) {
        return permissions.channelPermissions[channelId].includes(permission);
      }

      return false;
    },
    [permissions],
  );

  const hasAnyPermission = useCallback(
    (perms: PermissionName[], channelId?: string): boolean => {
      return perms.some((perm) => hasPermission(perm, channelId));
    },
    [hasPermission],
  );

  return {
    permissions,
    loading,
    hasPermission,
    hasAnyPermission,
    refetch: fetchPermissions,
  };
}
